import { Text, Card, Section, LoadingSpinner, Heading, Dialog } from '@soteria/design-system-ve';
import { useState, useEffect } from 'react';

import type { FlexibleAuditSession } from '../../domain/graphql-types';
import { useGetAuditSessionStats, type AuditSessionStats } from '../../hooks/useGetAuditSessionStats';

import * as styles from './CompleteAuditDialog.css';

interface CompleteAuditDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  auditSession: FlexibleAuditSession | null;
}

/**
 * Renders the loading state for the dialog
 */
function LoadingState() {
  return (
    <Card>
      <Section spacing="medium">
        <div className={styles.loadingContainer}>
          <LoadingSpinner />
          <Text>Loading audit session statistics...</Text>
        </div>
      </Section>
    </Card>
  );
}

/**
 * Renders the error state for the dialog
 */
function ErrorState() {
  return (
    <Card>
      <Section spacing="medium">
        <Text className={styles.errorMessage}>Failed to load audit session statistics. Please try again.</Text>
      </Section>
    </Card>
  );
}

/**
 * Renders the statistics grid
 */
function StatsGrid({ stats }: { stats: AuditSessionStats }) {
  const hasIncompleteItems = stats.incompleteCount > 0;

  return (
    <Card>
      <Section spacing="medium">
        <div className={styles.statsGrid}>
          <div className={styles.statItem}>
            <Text className={styles.statLabel}>Total Items</Text>
            <Text className={styles.statValue}>{stats.totalCount}</Text>
          </div>

          <div className={styles.statItem}>
            <Text className={styles.statLabel}>Completed</Text>
            <Text className={styles.statValue}>{stats.completeCount}</Text>
          </div>

          <div className={styles.statItem}>
            <Text className={styles.statLabel}>Skipped</Text>
            <Text className={styles.statValue}>{stats.skippedCount}</Text>
          </div>

          <div className={styles.statItem}>
            <Text className={styles.statLabel}>Remaining</Text>
            <Text className={`${styles.statValue} ${hasIncompleteItems ? styles.statValueWarning : ''}`}>{stats.incompleteCount}</Text>
          </div>
        </div>
      </Section>
    </Card>
  );
}

/**
 * Renders the success card when ready to complete
 */
function SuccessCard() {
  return (
    <Card className={styles.successCard}>
      <Section spacing="medium">
        <div className={styles.successContainer}>
          <Text className={styles.successTitle}>Ready to Complete</Text>
          <Text className={styles.successMessage}>All audit items have been processed. The audit session is ready to be completed.</Text>
        </div>
      </Section>
    </Card>
  );
}

/**
 * Complete Audit Dialog
 *
 * Shows a summary of the audit session before completion, including
 * total count, completed/skipped counts, and remaining items.
 * Displays whether the session can be completed based on the API response.
 */
// eslint-disable-next-line complexity
export function CompleteAuditDialog({ isOpen, onClose, onConfirm, auditSession }: CompleteAuditDialogProps) {
  const [isCompleting, setIsCompleting] = useState(false);
  const { stats, loading, error, refetch, utils } = useGetAuditSessionStats(auditSession?.id);

  // Reset state and refetch stats when dialog opens
  useEffect(() => {
    if (isOpen) {
      setIsCompleting(false);
      // Refetch stats to ensure we have the latest data
      if (auditSession?.id) {
        refetch();
      }
    }
  }, [isOpen, auditSession?.id, refetch]);

  const handleComplete = async () => {
    if (!auditSession || !stats?.canComplete) return;

    setIsCompleting(true);
    try {
      await onConfirm();
    } finally {
      setIsCompleting(false);
    }
  };

  if (!auditSession) {
    return null;
  }

  const showLoadingState = loading || !stats;
  const canComplete = stats?.canComplete ?? false;
  const hasIncompleteItems = utils?.hasIncompleteItems() ?? false;

  return (
    <Dialog
      cancelAction={{
        label: 'Cancel',
        disabled: isCompleting,
      }}
      description="Review the audit session summary before completing."
      isOpen={isOpen}
      primaryAction={{
        label: isCompleting ? <LoadingSpinner /> : 'Complete Audit',
        onClick: handleComplete,
        variant: 'primary',
        disabled: isCompleting || !canComplete || showLoadingState,
      }}
      size="md"
      title="Complete Audit Session"
      onClose={onClose}
    >
      <div className={styles.dialogContent}>
        <div className={styles.contentSection}>
          <Heading level="h3">Audit Summary</Heading>
        </div>

        {showLoadingState ? (
          <div className={styles.contentSection}>
            <LoadingState />
          </div>
        ) : error ? (
          <div className={styles.contentSection}>
            <ErrorState />
          </div>
        ) : (
          <>
            <div className={styles.contentSection}>
              <StatsGrid stats={stats} />
            </div>
            {hasIncompleteItems && (
              <div className={styles.contentSection}>
                <Text className={styles.incompleteMessage}>
                  There are {stats.incompleteCount} more items to complete.
                  {canComplete
                    ? ' You can finish the audit session anyway, but these items will remain unreviewed.'
                    : ' You must complete or skip all items before the audit session can be finalized.'}
                </Text>
              </div>
            )}
            {canComplete && !hasIncompleteItems && (
              <div className={styles.contentSection}>
                <SuccessCard />
              </div>
            )}
          </>
        )}
      </div>
    </Dialog>
  );
}
